import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, X, Eye, FileText, Bank, CurrencyInr, Clock, ArrowSquareOut } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { confirm } from '../components/ConfirmDialog';
import SummaryCards, { inr } from '../components/SummaryCards';
import PipelineToolbar from '../components/PipelineToolbar';
import { computeCloseWindow, withinCloseWindow, sortRows } from '../lib/pipelineFilters';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { PowerTable } from '../components/PowerTable';
import { useTenantPath } from '../lib/tenantPath';
import { fileUrl } from '../lib/fileUrl';

import { notify } from '../lib/notify';
import { PageSkeleton } from '../components/Skeletons';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Approvals = () => {
  const [quotationApprovals, setQuotationApprovals] = useState([]);
  const [productApprovals, setProductApprovals] = useState([]);
  const [lordReviews, setLordReviews] = useState([]);
  const [pendingBankAccounts, setPendingBankAccounts] = useState([]);
  // Feb 2026 (iter88) — Proforma Invoice + GST Exemption approvals now live
  // inside "My Approvals" instead of their own sidebar entries. Kept as
  // separate state so the tab-count badges stay live.
  const [piApprovals, setPiApprovals] = useState([]);
  const [gstExemptions, setGstExemptions] = useState([]);
  const [piBusyId, setPiBusyId] = useState(null);
  const [gstBusyId, setGstBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  // Approval-queue urgency signal — total value + oldest days waiting
  const [approvalSummary, setApprovalSummary] = useState(null);
  const [approvalSummaryLoading, setApprovalSummaryLoading] = useState(true);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  // iter127 — Inline preview drawer: shows the full quote (items + terms + GM)
  // without navigating away. Holds the quotation object as-is from the list.
  const [previewQuote, setPreviewQuote] = useState(null);
  const [action, setAction] = useState(null);
  const [comments, setComments] = useState('');
  const [activeTab, setActiveTab] = useState('quotations');
  const [viewMode, setViewMode] = useViewMode('approvals');
  // Pipeline toolbar — sort/filter the quotation approval queue.
  // Default: priority sort so high-value + close-imminent quotes bubble first.
  const [closeWindow, setCloseWindow] = useState('all');
  const [sortBy, setSortBy] = useState('priority');
  // Feb 2026 — Approval-reasons panel: toggles per-row so multiple can be
  // open at once (useful when doing a batch review side-by-side).
  const [expandedReasonIds, setExpandedReasonIds] = useState({});
  const tp = useTenantPath();
  const [selectedLord, setSelectedLord] = useState(null);
  const [showLordModal, setShowLordModal] = useState(false);
  const [lordComment, setLordComment] = useState('');
  const [lordAction, setLordAction] = useState('approve');
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const canApproveProducts = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const canApproveBankAccounts = ['Vice President', 'President'].includes(user?.role);
  const canSeeGm = ['Vice President', 'President'].includes(user?.role);

  // GM helpers — mirror the backend `cogs_at_sale` rule: exclude items where the snapshot is 0.
  // For quotes that are still pending approval, `cogs_at_sale` will be 0 on all items (snapshot
  // only happens at FINAL approval). To help VP/President make a call before approving, we
  // fall back to the current product.cogs_price as a live estimate.
  const [productCogsMap, setProductCogsMap] = useState({});  // { product_id: cogs_price }
  useEffect(() => {
    if (!canSeeGm) return;
    // Fetch all products once to build the id → cogs_price map. Sales users would see 0 here
    // (server strips it), but VP/President see real values.
    axios.get(`${API}/products?show_all=true`)
      .then(r => {
        const m = {};
        (r.data || []).forEach(p => { m[p.id] = Number(p.cogs_price || 0); });
        setProductCogsMap(m);
      })
      .catch(() => {});
  }, [canSeeGm]);

  const calcItemGm = (item) => {
    const revenue = Number(item.discounted_amount) > 0
      ? Number(item.discounted_amount)
      : Math.max(Number(item.quantity || 0) * Number(item.price || 0) - Number(item.item_discount_amount || 0), 0);
    // Prefer snapshot; fall back to current live COGS
    const cogsUnit = Number(item.cogs_at_sale) > 0 ? Number(item.cogs_at_sale) : (productCogsMap[item.product_id] || 0);
    const cogsTotal = cogsUnit * Number(item.quantity || 0);
    if (revenue <= 0 || cogsUnit <= 0) return { revenue, cogsTotal, gmAmount: null, gmPct: null };
    const gmAmount = revenue - cogsTotal;
    const gmPct = revenue > 0 ? (gmAmount / revenue) * 100 : 0;
    return { revenue, cogsTotal, gmAmount, gmPct };
  };

  const calcQuoteGm = (quotation) => {
    let rev = 0, cogs = 0, missing = 0;
    (quotation.items || []).forEach(it => {
      const g = calcItemGm(it);
      if (g.gmAmount == null) { missing += 1; return; }
      rev += g.revenue;
      cogs += g.cogsTotal;
    });
    if (rev <= 0) return { rev, cogs, gmAmount: 0, gmPct: 0, missing };
    return { rev, cogs, gmAmount: rev - cogs, gmPct: ((rev - cogs) / rev) * 100, missing };
  };

  useEffect(() => {
    fetchQuotationApprovals();
    fetchLordReviews();
    fetchApprovalSummary();
    fetchPiApprovals();
    fetchGstExemptions();
    if (canApproveProducts) {
      fetchProductApprovals();
    }
    if (canApproveBankAccounts) {
      fetchPendingBankAccounts();
    }
  }, [canApproveProducts, canApproveBankAccounts]);

  const fetchQuotationApprovals = async () => {
    try {
      const response = await axios.get(`${API}/my-approvals`);
      setQuotationApprovals(response.data);
    } catch (error) {
      console.error('Error fetching quotation approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dollar value + oldest-days summary for the queue owned by THIS user's
  // role. Refetched every time we approve/reject so the top cards stay live.
  const fetchApprovalSummary = async () => {
    setApprovalSummaryLoading(true);
    try {
      const r = await axios.get(`${API}/my-approvals/summary`);
      setApprovalSummary(r.data);
    } catch (e) {
      console.error('Error fetching approval summary:', e);
      setApprovalSummary(null);
    } finally {
      setApprovalSummaryLoading(false);
    }
  };

  const fetchProductApprovals = async () => {
    try {
      const response = await axios.get(`${API}/products/pending`);
      setProductApprovals(response.data);
    } catch (error) {
      console.error('Error fetching product approvals:', error);
    }
  };

  const fetchLordReviews = async () => {
    try {
      const response = await axios.get(`${API}/lord/pending`);
      setLordReviews(response.data);
    } catch (error) {
      console.error('Error fetching LORD reviews:', error);
    }
  };

  const fetchPendingBankAccounts = async () => {
    try {
      const response = await axios.get(`${API}/customers/pending-bank-accounts`);
      setPendingBankAccounts(response.data);
    } catch (error) {
      console.error('Error fetching pending bank accounts:', error);
    }
  };

  // iter88 — Proforma Invoice queue owned by THIS user's role.
  const fetchPiApprovals = async () => {
    try {
      const r = await axios.get(`${API}/proforma-invoices`, { params: { scope: 'pending' } });
      setPiApprovals(r.data?.items || []);
    } catch (e) {
      console.error('Error fetching PI approvals:', e);
    }
  };

  // iter88 — GST Exemption pending queue.
  const fetchGstExemptions = async () => {
    try {
      const r = await axios.get(`${API}/gst-exemptions`, { params: { scope: 'pending' } });
      // Backend returns `{items: [...]}` — handle both shapes defensively.
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setGstExemptions(list);
    } catch (e) {
      console.error('Error fetching GST exemptions:', e);
    }
  };

  const handlePiDecision = async (pi, decisionAction) => {
    const label = decisionAction === 'approve' ? 'Approve' : 'Reject';
    let reason = '';
    // Reject requires a reason server-side; Approve doesn't.
    if (decisionAction === 'reject') {
      const r = window.prompt(
        `Reject Proforma Invoice ${pi.pi_number}?\n\n` +
        `Amount ₹${Number(pi.total_amount || 0).toLocaleString('en-IN')} · ${pi.customer_snapshot?.organisation_name || 'Customer'}\n\n` +
        `Enter a rejection reason (required):`,
        '',
      );
      if (r == null) return;                 // cancelled
      reason = (r || '').trim();
      if (!reason) { notify('Reason is required.'); return; }
    } else {
      const ok = await confirm({
        title: `Approve Proforma Invoice ${pi.pi_number}?`,
        message: `Amount ₹${Number(pi.total_amount || 0).toLocaleString('en-IN')} · ${pi.customer_snapshot?.organisation_name || 'Customer'}`,
        confirmText: label,
      });
      if (!ok) return;
    }
    setPiBusyId(pi.id);
    try {
      await axios.post(`${API}/proforma-invoices/${pi.id}/${decisionAction}`, { reason });
      await fetchPiApprovals();
    } catch (e) {
      notify(e.response?.data?.detail || 'PI decision failed');
    } finally {
      setPiBusyId(null);
    }
  };

  const handleGstDecision = async (req, decisionAction) => {
    const label = decisionAction === 'approve' ? 'Approve' : 'Reject';
    // GST approve/reject requires a reason — prompt inline. `window.prompt`
    // keeps this feature light without a new modal; the standalone
    // GstExemptions.js page has a richer form for the same action.
    const reason = window.prompt(
      `${label} GST Exemption for "${req.organisation_name || req.customer_name || 'this customer'}".\n\n` +
      `Enter a ${label.toLowerCase()} reason (required):`,
      '',
    );
    if (reason == null) return;              // user cancelled
    const trimmed = reason.trim();
    if (!trimmed) { notify('Reason is required.'); return; }
    setGstBusyId(req.id);
    try {
      await axios.post(`${API}/gst-exemptions/${req.id}/${decisionAction}`, { reason: trimmed });
      await fetchGstExemptions();
    } catch (e) {
      notify(e.response?.data?.detail || 'GST decision failed');
    } finally {
      setGstBusyId(null);
    }
  };

  const handleApproveBankAccount = async (customerId, accountNumber) => {
    try {
      await axios.post(`${API}/customers/${customerId}/approve-bank/${accountNumber}`);
      fetchPendingBankAccounts();
      notify('Bank account approved');
    } catch (error) {
      console.error('Error approving bank account:', error);
      notify(error.response?.data?.detail || 'Error approving bank account');
    }
  };

  const handleRejectBankAccount = async (customerId, accountNumber) => {
    const ok = await confirm({
      title: 'Reject bank account?',
      message: 'This bank account will be permanently removed from the customer.',
      confirmText: 'Reject',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/customers/${customerId}/reject-bank/${accountNumber}`);
      fetchPendingBankAccounts();
      notify('Bank account rejected and removed');
    } catch (error) {
      console.error('Error rejecting bank account:', error);
      notify(error.response?.data?.detail || 'Error rejecting bank account');
    }
  };

  // iter132 — Payment Terms approval queue (VP+ decide; NSM+ can view)
  const [paymentTermsApprovals, setPaymentTermsApprovals] = useState([]);
  const [paymentTermsCanDecide, setPaymentTermsCanDecide] = useState(false);
  const [paymentTermsBusyId, setPaymentTermsBusyId] = useState(null);
  const canSeePaymentTerms = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);

  const fetchPaymentTermsApprovals = async () => {
    try {
      const r = await axios.get(`${API}/payment-terms-approvals`, { params: { status: 'pending' } });
      setPaymentTermsApprovals(r.data?.items || []);
      setPaymentTermsCanDecide(!!r.data?.can_decide);
    } catch (e) {
      console.error('Error fetching payment terms approvals:', e);
    }
  };

  useEffect(() => {
    if (canSeePaymentTerms) fetchPaymentTermsApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSeePaymentTerms]);

  // iter132.4 — Bulk selection on payment terms tab
  const [paymentTermsSelected, setPaymentTermsSelected] = useState(new Set());
  const [paymentTermsBulkBusy, setPaymentTermsBulkBusy] = useState(false);
  useEffect(() => {
    // Reset selection whenever the list content changes so stale ids don't
    // linger through a refresh.
    setPaymentTermsSelected(new Set());
  }, [paymentTermsApprovals.length]);
  const togglePaymentTermsSelect = (id) => {
    setPaymentTermsSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };
  const togglePaymentTermsSelectAll = () => {
    if (paymentTermsSelected.size === paymentTermsApprovals.length) {
      setPaymentTermsSelected(new Set());
    } else {
      setPaymentTermsSelected(new Set(paymentTermsApprovals.map((r) => r.id)));
    }
  };
  const handlePaymentTermsBulk = async (decision) => {
    if (!paymentTermsCanDecide) return;
    const ids = Array.from(paymentTermsSelected);
    if (ids.length === 0) return;
    let reason = '';
    if (decision === 'reject') {
      const r = window.prompt(
        `Reject ${ids.length} payment-terms request${ids.length === 1 ? '' : 's'}?\n\n` +
        `Enter a rejection reason (required, applied to all):`,
        '',
      );
      if (r == null) return;
      reason = (r || '').trim();
      if (!reason) { notify('Reason is required.'); return; }
    } else {
      const ok = await confirm({
        title: `Approve ${ids.length} payment-terms request${ids.length === 1 ? '' : 's'}?`,
        message: 'Each request will be marked Won (or its edit applied) under the original requester\'s identity.',
        confirmText: 'Approve All',
      });
      if (!ok) return;
    }
    setPaymentTermsBulkBusy(true);
    try {
      const resp = await axios.post(`${API}/payment-terms-approvals/bulk`, {
        ids, decision, reason,
      });
      await fetchPaymentTermsApprovals();
      const failed = (resp.data?.results || []).filter((r) => r.status === 'failed');
      if (failed.length > 0) {
        const detail = failed.slice(0, 5).map((r) => `• ${r.id.slice(0, 8)}: ${r.detail}`).join('\n');
        notify(`${resp.data.message}\n\n${failed.length} failed:\n${detail}`);
      } else {
        // Silent success — the queue count in the tab label already reflects the drain.
      }
    } catch (e) {
      notify(e.response?.data?.detail || `Bulk ${decision} failed`);
    } finally {
      setPaymentTermsBulkBusy(false);
    }
  };

  const handlePaymentTermsDecision = async (req, decisionAction) => {
    let reason = '';
    if (decisionAction === 'reject') {
      const r = window.prompt(
        `Reject payment terms request for "${req.lead_name || 'lead'}"?\n\n` +
        `Terms: ${req.payment_terms_summary || ''}\n\n` +
        `Enter a rejection reason (required):`,
        '',
      );
      if (r == null) return;
      reason = (r || '').trim();
      if (!reason) { notify('Reason is required.'); return; }
    } else {
      const ok = await confirm({
        title: 'Approve payment terms?',
        message: `Terms: ${req.payment_terms_summary || ''}\nLead will be marked Won using these terms.`,
        confirmText: 'Approve',
      });
      if (!ok) return;
    }
    setPaymentTermsBusyId(req.id);
    try {
      await axios.post(`${API}/payment-terms-approvals/${req.id}/${decisionAction}`, { reason });
      await fetchPaymentTermsApprovals();
    } catch (e) {
      notify(e.response?.data?.detail || 'Payment terms decision failed');
    } finally {
      setPaymentTermsBusyId(null);
    }
  };

  const handleReviewLord = (lord, reviewAction = 'approve') => {
    setSelectedLord(lord);
    setLordComment('');
    setLordAction(reviewAction);
    setShowLordModal(true);
  };

  const submitLordReview = async () => {
    try {
      await axios.post(`${API}/lord/${selectedLord.id}/review`, {
        action: lordAction,
        comments: lordComment
      });
      fetchLordReviews();
      setShowLordModal(false);
      notify(`LORD record ${lordAction === 'approve' ? 'reviewed' : 'rejected'} successfully`);
    } catch (error) {
      console.error('Error reviewing LORD:', error);
      notify('Error reviewing LORD record');
    }
  };

  const handleApproveQuotation = (quotation, approvalAction) => {
    setSelectedQuotation(quotation);
    setAction(approvalAction);
    setShowModal(true);
  };

  const submitQuotationApproval = async () => {
    try {
      await axios.post(`${API}/quotations/${selectedQuotation.id}/approve`, {
        action,
        comments
      });
      fetchQuotationApprovals();
      fetchApprovalSummary();
      setShowModal(false);
      setComments('');
      notify(`Quotation ${action}d successfully`);
    } catch (error) {
      console.error('Error submitting approval:', error);
      notify('Error submitting approval');
    }
  };

  const handleApproveProduct = async (productId) => {
    try {
      await axios.post(`${API}/products/${productId}/approve`);
      notify('Product approved successfully');
      fetchProductApprovals();
    } catch (error) {
      console.error('Error approving product:', error);
      notify('Error approving product');
    }
  };

  const handleRejectProduct = async (productId) => {
    const ok = await confirm({
      title: 'Reject product?',
      message: 'Are you sure you want to reject this product? This will permanently delete it.',
      confirmText: 'Reject',
      danger: true,
    });
    if (!ok) return;
    try {
      await axios.post(`${API}/products/${productId}/reject`);
      notify('Product rejected');
      fetchProductApprovals();
    } catch (error) {
      console.error('Error rejecting product:', error);
      notify('Error rejecting product');
    }
  };

  if (loading) {
    return <PageSkeleton />;
  }

  const totalApprovals = quotationApprovals.length + (canApproveProducts ? productApprovals.length : 0) + lordReviews.length;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="approvals-page">
      <div className="mb-8">
        <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="approvals-title">
          My Approvals
        </h1>
        <p className="text-[#6D8E87]">Review and approve pending items</p>
      </div>

      {/* Urgency signal — total quotation $ waiting on YOUR call, plus the
          oldest item's age. Nudges faster reviews when the queue backs up. */}
      <SummaryCards
        testid="approvals-summary"
        loading={approvalSummaryLoading}
        items={approvalSummary ? [
          {
            label: 'Pending quotations',
            value: approvalSummary.pending_count,
            icon: FileText,
            sub: 'awaiting your review',
            testid: 'summary-pending-count',
          },
          {
            label: 'Total value pending',
            value: inr(approvalSummary.total_pending_value),
            icon: CurrencyInr,
            tone: approvalSummary.total_pending_value > 0 ? 'warning' : 'default',
            sub: 'across all queued items',
            testid: 'summary-pending-value',
          },
          {
            label: 'Oldest waiting',
            value: `${approvalSummary.oldest_pending_days}d`,
            icon: Clock,
            tone: approvalSummary.oldest_pending_days >= 7 ? 'danger' :
                  approvalSummary.oldest_pending_days >= 3 ? 'warning' : 'default',
            sub: approvalSummary.oldest_pending_days >= 7 ? 'act fast — client waiting'
                 : approvalSummary.oldest_pending_days >= 3 ? 'starting to age' : 'in good shape',
            testid: 'summary-oldest-pending',
          },
        ] : []}
      />

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-[#D5E8E4]">
        <button
          onClick={() => setActiveTab('quotations')}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === 'quotations'
              ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
              : 'text-[#6D8E87] hover:text-[#3B5A54]'
          }`}
          data-testid="quotations-tab"
        >
          Quotations ({quotationApprovals.length})
        </button>
        {canApproveProducts && (
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'products'
                ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
                : 'text-[#6D8E87] hover:text-[#3B5A54]'
            }`}
            data-testid="products-tab"
          >
            Products ({productApprovals.length})
          </button>
        )}
        <button
          onClick={() => setActiveTab('lord')}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === 'lord'
              ? 'text-red-600 border-b-2 border-red-500'
              : 'text-[#6D8E87] hover:text-[#3B5A54]'
          }`}
          data-testid="lord-tab"
        >
          LORD Reviews ({lordReviews.length})
        </button>
        {canApproveBankAccounts && (
          <button
            onClick={() => setActiveTab('bank')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'bank'
                ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
                : 'text-[#6D8E87] hover:text-[#3B5A54]'
            }`}
            data-testid="bank-accounts-tab"
          >
            Bank Accounts ({pendingBankAccounts.reduce((sum, c) => sum + c.pending_accounts.length, 0)})
          </button>
        )}
        <button
          onClick={() => setActiveTab('pi')}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === 'pi'
              ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
              : 'text-[#6D8E87] hover:text-[#3B5A54]'
          }`}
          data-testid="pi-tab"
        >
          Proforma Invoices ({piApprovals.length})
        </button>
        <button
          onClick={() => setActiveTab('gst')}
          className={`px-6 py-3 font-medium transition-all ${
            activeTab === 'gst'
              ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
              : 'text-[#6D8E87] hover:text-[#3B5A54]'
          }`}
          data-testid="gst-exemptions-tab"
        >
          GST Exemptions ({gstExemptions.length})
        </button>
        {canSeePaymentTerms && (
          <button
            onClick={() => setActiveTab('payment-terms')}
            className={`px-6 py-3 font-medium transition-all ${
              activeTab === 'payment-terms'
                ? 'text-[#00a69c] border-b-2 border-[#00a69c]'
                : 'text-[#6D8E87] hover:text-[#3B5A54]'
            }`}
            data-testid="payment-terms-tab"
          >
            Payment Terms ({paymentTermsApprovals.length})
          </button>
        )}
      </div>

      {/* Quotations Tab Content */}
      {activeTab === 'quotations' && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <PipelineToolbar
              pageKey="approvals"
              window={closeWindow}
              onWindowChange={setCloseWindow}
              sortBy={sortBy}
              onSortChange={setSortBy}
              showForecastToggle={false}
            />
            <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="approvals-view" />
          </div>
          {(() => {
            let list = quotationApprovals;
            if (closeWindow && closeWindow !== 'all') {
              const win = computeCloseWindow(closeWindow);
              list = list.filter(q => withinCloseWindow(q, 'expected_closing_date', win));
            }
            list = sortRows(list, sortBy, { valueKey: 'total', dateKey: 'expected_closing_date' });
            if (list.length === 0) return (
              <div className="medical-card p-12 text-center" data-testid="no-quotation-approvals">
                <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-[#00a69c]" weight="bold" />
                </div>
                <h3 className="text-xl font-semibold text-[#052A23] mb-2">All Caught Up!</h3>
                <p className="text-[#6D8E87]">No pending quotation approvals</p>
              </div>
            );
            if (viewMode === 'list') {
              return (
                <PowerTable
                  testIdPrefix="approvals-table"
                  onRowClick={(row) => navigate(`/quotations/${row.id}`)}
                  initialSort={{ key: 'total', dir: 'desc' }}
                  rows={list}
                  columns={[
                    { key: 'lead_name', label: 'Lead', sortable: true, cell: (r) => (
                      <div>
                        <div className="font-semibold text-[#052A23]">{r.lead_name}</div>
                        <div className="text-[11px] text-[#6D8E87]">{r.customer_name || r.company || '—'}</div>
                      </div>
                    ) },
                    { key: 'total', label: 'Total', sortable: true, className: 'text-right',
                      accessor: (r) => Number(r.total || 0),
                      cell: (r) => <span className="font-semibold text-[#00a69c]">₹{Number(r.total || 0).toLocaleString('en-IN')}</span> },
                    { key: 'current_approval_stage', label: 'Awaiting', sortable: true, cell: (r) => (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{r.current_approval_stage}</span>
                    ) },
                    { key: 'created_by_name', label: 'Created By', sortable: true },
                    { key: 'created_by_role', label: 'Role', sortable: true, cell: (r) => r.created_by_role || '—' },
                    { key: 'approval_reasons', label: 'Why', cell: (r) => {
                      const reasons = r.approval_reasons || [];
                      if (!reasons.length) return <span className="text-xs text-[#6D8E87]">—</span>;
                      return (
                        <span className="text-xs text-rose-700 font-medium">
                          {reasons[0].label}{reasons.length > 1 ? ` +${reasons.length - 1}` : ''}
                        </span>
                      );
                    } },
                    { key: 'expected_closing_date', label: 'Close By', sortable: true,
                      cell: (r) => r.expected_closing_date ? new Date(r.expected_closing_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—' },
                  ]}
                />
              );
            }
            return (
              <div className="space-y-4" data-testid="quotation-approvals-list">
                {list.map((quotation) => (
                  <div key={quotation.id} className="medical-card p-6" data-testid={`approval-item-${quotation.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                          {quotation.lead_name}
                        </h3>
                        <span className="status-badge status-pending">
                          {quotation.current_approval_stage}
                        </span>
                        {/* Feb 2026 — "Why it needs you" chip + expand. First
                            reason shown inline; click to open the full list
                            with details. Colour by type: rose for discount,
                            amber for T&C stretches, indigo for payment. */}
                        {quotation.approval_reasons && quotation.approval_reasons.length > 0 && (() => {
                          const reasons = quotation.approval_reasons;
                          const first = reasons[0];
                          const extra = reasons.length - 1;
                          const toneMap = {
                            discount: 'bg-rose-50 text-rose-700 border-rose-300',
                            warranty: 'bg-amber-50 text-amber-800 border-amber-300',
                            installation: 'bg-amber-50 text-amber-800 border-amber-300',
                            delivery: 'bg-amber-50 text-amber-800 border-amber-300',
                            payment: 'bg-indigo-50 text-indigo-700 border-indigo-300',
                          };
                          const isOpen = !!expandedReasonIds[quotation.id];
                          return (
                            <button
                              type="button"
                              onClick={() => setExpandedReasonIds(prev => ({ ...prev, [quotation.id]: !prev[quotation.id] }))}
                              data-testid={`why-chip-${quotation.id}`}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${toneMap[first.type] || 'bg-slate-50 text-slate-700 border-slate-300'} hover:brightness-95`}
                              title="Why this needs your approval — click for details"
                            >
                              Why: {first.label}
                              {extra > 0 && <span className="ml-1 opacity-80">+{extra} more</span>}
                              <span className="ml-1 opacity-60 text-[9px]">{isOpen ? '▲' : '▼'}</span>
                            </button>
                          );
                        })()}
                        {/* Feb 2026 — persistent "View full quote" link so
                            approvers can always drill into the full quote
                            without hunting through expandable panels. */}
                        <button
                          type="button"
                          onClick={() => setPreviewQuote(quotation)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[#00a69c] bg-[#00a69c] text-white hover:bg-[#00857e] transition"
                          data-testid={`preview-quote-${quotation.id}`}
                          title="Open inline preview — approve/reject from here"
                        >
                          <Eye size={12} weight="bold" /> Preview
                        </button>
                        <a
                          href={tp(`/quotations/${quotation.id}`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-[#00a69c] text-[#00857e] bg-white hover:bg-[#F4F9F7] transition"
                          data-testid={`view-full-quote-${quotation.id}`}
                          title="Open the full quotation in a new tab"
                        >
                          View full quote →
                        </a>
                      </div>

                      {/* Expanded reason panel — inline so approvers stay in flow. */}
                      {expandedReasonIds[quotation.id] && quotation.approval_reasons?.length > 0 && (
                        <div
                          className="mb-4 p-3 border border-[#D5E8E4] rounded-lg bg-[#F9FCFB]"
                          data-testid={`why-panel-${quotation.id}`}
                        >
                          <div className="text-[11px] uppercase tracking-wide text-[#6D8E87] font-bold mb-2">
                            Reasons for approval
                          </div>
                          <ul className="space-y-1.5">
                            {quotation.approval_reasons.map((r, idx) => (
                              <li key={idx} className="text-sm text-[#3B5A54]" data-testid={`why-reason-${quotation.id}-${idx}`}>
                                <span className="font-semibold text-[#052A23]">{r.label}</span>
                                <span className="text-[#6D8E87]"> — {r.detail}</span>
                              </li>
                            ))}
                          </ul>
                          <a
                            href={tp(`/quotations/${quotation.id}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-block mt-3 text-xs text-[#00a69c] hover:underline font-semibold"
                            data-testid={`view-quote-link-${quotation.id}`}
                          >
                            View full quotation in a new tab →
                          </a>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                        <div>
                          <p className="text-xs text-[#6D8E87] mb-1">Quotation ID</p>
                          <a
                            href={tp(`/quotations/${quotation.id}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium text-[#00857e] hover:underline font-mono"
                            title={`Open quotation ${quotation.id}`}
                            data-testid={`quote-id-link-${quotation.id}`}
                          >
                            {quotation.id.slice(0, 8)} ↗
                          </a>
                        </div>
                        <div>
                          <p className="text-xs text-[#6D8E87] mb-1">Total Amount</p>
                          <p className="text-lg font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>
                            ₹{quotation.total.toLocaleString('en-IN')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-[#6D8E87] mb-1">Created By</p>
                          <p className="text-sm font-medium text-[#3B5A54]">{quotation.created_by_name}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-[#6D8E87] mb-2">Items</p>
                        <div className="space-y-1">
                          {quotation.items.map((item, index) => {
                            const g = canSeeGm ? calcItemGm(item) : null;
                            return (
                              <div key={index} className="flex justify-between items-center text-sm">
                                <span className="text-[#3B5A54]">
                                  {item.product_name} x {item.quantity}
                                </span>
                                <div className="flex items-center gap-3">
                                  {canSeeGm && g && g.gmPct != null && (
                                    <span
                                      className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                        g.gmPct >= 30 ? 'bg-[#D1FAE5] text-[#065F46]'
                                          : g.gmPct >= 15 ? 'bg-[#FEF3C7] text-[#92400E]'
                                          : 'bg-[#FEE2E2] text-[#991B1B]'
                                      }`}
                                      data-testid={`gm-item-${item.product_id}`}
                                      title={`Revenue ₹${g.revenue.toFixed(0)} · COGS ₹${g.cogsTotal.toFixed(0)}`}
                                    >
                                      GM {g.gmPct.toFixed(1)}%
                                    </span>
                                  )}
                                  <span className="font-medium text-[#052A23]">₹{item.total.toLocaleString('en-IN')}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {canSeeGm && (() => {
                          const g = calcQuoteGm(quotation);
                          const showable = g.rev > 0 && g.cogs > 0;
                          return (
                            <div className="mt-3 p-2.5 rounded-lg bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] border border-[#F59E0B]/30" data-testid={`gm-quote-${quotation.id}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <span className="text-[10px] uppercase tracking-wider font-bold text-[#92400E]">Executive Margin Preview</span>
                                {showable ? (
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className="text-[#3B5A54]">Revenue <b>₹{g.rev.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</b></span>
                                    <span className="text-[#3B5A54]">COGS <b>₹{g.cogs.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</b></span>
                                    <span className={`font-bold ${g.gmPct >= 30 ? 'text-[#065F46]' : g.gmPct >= 15 ? 'text-[#92400E]' : 'text-[#991B1B]'}`}>
                                      GM {g.gmPct.toFixed(1)}% · ₹{g.gmAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="text-[11px] text-[#92400E]">COGS not set on {g.missing} item{g.missing === 1 ? '' : 's'} — margin unavailable.</span>
                                )}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-6 pt-6 border-t border-[#D5E8E4]">
                    <button
                      onClick={() => handleApproveQuotation(quotation, 'reject')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-50 text-[#EF4444] rounded-full font-medium hover:bg-red-100 transition"
                      data-testid={`reject-quotation-${quotation.id}`}
                    >
                      <X size={20} weight="bold" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleApproveQuotation(quotation, 'approve')}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#00a69c] text-white rounded-full font-medium hover:bg-[#00bfb3] transition"
                      data-testid={`approve-quotation-${quotation.id}`}
                    >
                      <Check size={20} weight="bold" />
                      Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
          })()}
        </>
      )}

      {/* Products Tab Content */}
      {activeTab === 'products' && canApproveProducts && (
        <>
          {productApprovals.length === 0 ? (
            <div className="medical-card p-12 text-center" data-testid="no-product-approvals">
              <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-[#00a69c]" weight="bold" />
              </div>
              <h3 className="text-xl font-semibold text-[#052A23] mb-2">All Caught Up!</h3>
              <p className="text-[#6D8E87]">No pending product approvals</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-approvals-grid">
              {productApprovals.map((product) => (
                <div key={product.id} className="medical-card p-6" data-testid={`product-approval-${product.id}`}>
                  <div className="h-40 bg-[#E2F3F0] rounded-lg mb-4 flex items-center justify-center">
                    {product.images.length > 0 ? (
                      <img
                        src={fileUrl(product.images[0])}
                        alt={product.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <img
                        src="https://images.unsplash.com/photo-1641877492086-771bc14a8e5e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwyfHxob3NwaXRhbCUyMGZ1cm5pdHVyZXxlbnwwfHx8fDE3NzU3MTczNTl8MA&ixlib=rb-4.1.0&q=85"
                        alt={product.name}
                        className="w-full h-full object-cover rounded-lg opacity-50"
                      />
                    )}
                  </div>

                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-[#FEF3C7] text-[#92400E] text-xs rounded-full mb-2">
                      PENDING APPROVAL
                    </span>
                    <h3 className="text-xl font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>
                      {product.name}
                    </h3>
                    <p className="text-xs text-[#6D8E87] mb-2">{product.category}</p>
                    <p className="text-sm text-[#3B5A54] mb-3 line-clamp-2">{product.description}</p>
                    <div className="text-2xl font-bold text-[#00a69c] mb-2" style={{ fontFamily: 'Outfit' }}>
                      ₹{product.price.toLocaleString('en-IN')}
                    </div>
                  </div>

                  <div className="border-t border-[#D5E8E4] pt-3 mb-4">
                    <p className="text-xs text-[#6D8E87]">Submitted by</p>
                    <p className="text-sm font-medium text-[#3B5A54]">{product.created_by_name}</p>
                    <p className="text-xs text-[#6D8E87]">{new Date(product.created_at).toLocaleDateString()}</p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleRejectProduct(product.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-[#EF4444] rounded-lg hover:bg-red-100 transition"
                      data-testid={`reject-product-${product.id}`}
                    >
                      <X size={20} weight="bold" />
                      Reject
                    </button>
                    <button
                      onClick={() => handleApproveProduct(product.id)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-[#00a69c] text-white rounded-lg hover:bg-[#00bfb3] transition"
                      data-testid={`approve-product-${product.id}`}
                    >
                      <Check size={20} weight="bold" />
                      Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Proforma Invoices Tab (iter88) */}
      {activeTab === 'pi' && (
        <div className="medical-card overflow-hidden" data-testid="pi-approvals-panel">
          {piApprovals.length === 0 ? (
            <div className="p-10 text-center text-[#6D8E87]" data-testid="pi-approvals-empty">
              No Proforma Invoices waiting on you right now.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-widest text-[#6D8E87]">
                  <tr>
                    <th className="text-left px-4 py-3">PI Number</th>
                    <th className="text-left px-4 py-3">Quote</th>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-right px-4 py-3">Amount</th>
                    {canSeeGm && <th className="text-right px-4 py-3">GM %</th>}
                    <th className="text-left px-4 py-3">Created By</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {piApprovals.map((pi) => (
                    <tr key={pi.id} className="border-t border-[#E2ECEA] hover:bg-[#FAFDFC]" data-testid={`pi-approval-row-${pi.id}`}>
                      <td className="px-4 py-3 font-semibold text-[#052A23]">{pi.pi_number || '—'}</td>
                      <td className="px-4 py-3 text-[#3B5A54]">{pi.quotation_number || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="text-[#052A23]">{pi.customer_snapshot?.organisation_name || '—'}</div>
                        {pi.customer_snapshot?.customer_code && (
                          <div className="text-[11px] text-[#6D8E87]">{pi.customer_snapshot.customer_code}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-semibold text-[#00a69c]">
                        ₹{Number(pi.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </td>
                      {canSeeGm && (
                        <td className="px-4 py-3 text-right font-mono" data-testid={`pi-approval-gm-${pi.id}`}>
                          {typeof pi.gm_pct === 'number' ? (
                            <div className="inline-flex flex-col items-end leading-tight">
                              <span className={`font-semibold ${pi.gm_pct >= 25 ? 'text-emerald-700' : pi.gm_pct >= 15 ? 'text-amber-600' : 'text-rose-600'}`}>
                                {pi.gm_pct.toFixed(1)}%
                              </span>
                              {!pi.gm_complete && (
                                <span className="text-[9px] text-amber-600 font-semibold" title="Some line items are missing COGS — partial figure">partial</span>
                              )}
                            </div>
                          ) : <span className="text-[#6D8E87] text-xs">—</span>}
                        </td>
                      )}
                      <td className="px-4 py-3 text-[#3B5A54]">{pi.created_by_name || '—'}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => navigate(tp(`/proforma-invoices?open=${pi.id}`))}
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7]"
                          data-testid={`pi-approval-view-${pi.id}`}
                          title="View PDF and details"
                        >
                          <Eye size={12} weight="bold" /> View
                        </button>
                        <button
                          type="button"
                          disabled={piBusyId === pi.id}
                          onClick={() => handlePiDecision(pi, 'approve')}
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                          data-testid={`pi-approval-approve-${pi.id}`}
                        >
                          <Check size={12} weight="bold" /> Approve
                        </button>
                        <button
                          type="button"
                          disabled={piBusyId === pi.id}
                          onClick={() => handlePiDecision(pi, 'reject')}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                          data-testid={`pi-approval-reject-${pi.id}`}
                        >
                          <X size={12} weight="bold" /> Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* GST Exemptions Tab (iter88) */}
      {activeTab === 'gst' && (
        <div className="medical-card overflow-hidden" data-testid="gst-approvals-panel">
          {gstExemptions.length === 0 ? (
            <div className="p-10 text-center text-[#6D8E87]" data-testid="gst-approvals-empty">
              No GST exemption requests waiting on you right now.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-widest text-[#6D8E87]">
                  <tr>
                    <th className="text-left px-4 py-3">Customer</th>
                    <th className="text-left px-4 py-3">Reason</th>
                    <th className="text-left px-4 py-3">Requested By</th>
                    <th className="text-left px-4 py-3">Requested On</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {gstExemptions.map((req) => (
                    <tr key={req.id} className="border-t border-[#E2ECEA] hover:bg-[#FAFDFC]" data-testid={`gst-approval-row-${req.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#052A23]">{req.organisation_name || req.customer_name || '—'}</div>
                        {req.customer_gst_number && (
                          <div className="text-[11px] text-[#6D8E87]">{req.customer_gst_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[#3B5A54] max-w-[320px]">{req.reason || '—'}</td>
                      <td className="px-4 py-3 text-[#3B5A54]">{req.requester_name || '—'}</td>
                      <td className="px-4 py-3 text-[#3B5A54]">
                        {req.created_at ? new Date(req.created_at).toLocaleDateString('en-IN') : '—'}
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => navigate(tp('/gst-exemptions'))}
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7]"
                          data-testid={`gst-approval-view-${req.id}`}
                          title="Open full details"
                        >
                          <Eye size={12} weight="bold" /> View
                        </button>
                        <button
                          type="button"
                          disabled={gstBusyId === req.id}
                          onClick={() => handleGstDecision(req, 'approve')}
                          className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                          data-testid={`gst-approval-approve-${req.id}`}
                        >
                          <Check size={12} weight="bold" /> Approve
                        </button>
                        <button
                          type="button"
                          disabled={gstBusyId === req.id}
                          onClick={() => handleGstDecision(req, 'reject')}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                          data-testid={`gst-approval-reject-${req.id}`}
                        >
                          <X size={12} weight="bold" /> Reject
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'payment-terms' && canSeePaymentTerms && (
        <div className="medical-card overflow-hidden" data-testid="payment-terms-approvals-panel">
          {paymentTermsApprovals.length === 0 ? (
            <div className="p-10 text-center text-[#6D8E87]" data-testid="payment-terms-approvals-empty">
              No non-standard payment term requests waiting on you right now.
            </div>
          ) : (
            <>
              {paymentTermsCanDecide && (
                <div className="flex items-center justify-between gap-3 px-4 py-2 bg-[#F4F9F7] border-b border-[#E2ECEA]" data-testid="payment-terms-bulk-toolbar">
                  <div className="text-xs text-[#3B5A54]">
                    {paymentTermsSelected.size} of {paymentTermsApprovals.length} selected
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      disabled={paymentTermsBulkBusy || paymentTermsSelected.size === 0}
                      onClick={() => handlePaymentTermsBulk('approve')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="payment-terms-bulk-approve"
                    >
                      <Check size={12} weight="bold" /> Approve selected
                    </button>
                    <button
                      type="button"
                      disabled={paymentTermsBulkBusy || paymentTermsSelected.size === 0}
                      onClick={() => handlePaymentTermsBulk('reject')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="payment-terms-bulk-reject"
                    >
                      <X size={12} weight="bold" /> Reject selected
                    </button>
                  </div>
                </div>
              )}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-widest text-[#6D8E87]">
                  <tr>
                    {paymentTermsCanDecide && (
                      <th className="text-left px-3 py-3 w-8">
                        <input
                          type="checkbox"
                          checked={paymentTermsSelected.size > 0 && paymentTermsSelected.size === paymentTermsApprovals.length}
                          onChange={togglePaymentTermsSelectAll}
                          className="w-4 h-4 accent-[#00a69c]"
                          data-testid="payment-terms-select-all"
                          aria-label="Select all"
                        />
                      </th>
                    )}
                    <th className="text-left px-4 py-3">Lead / Customer</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">Requested Terms</th>
                    <th className="text-right px-4 py-3">Deal Value</th>
                    <th className="text-left px-4 py-3">Requested By</th>
                    <th className="text-left px-4 py-3">Age</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentTermsApprovals.map((req) => {
                    const daysWaiting = req.created_at ? Math.max(0, Math.floor((Date.now() - new Date(req.created_at).getTime()) / 86400000)) : 0;
                    const isEdit = req.request_type === 'edit';
                    return (
                      <tr key={req.id} className="border-t border-[#E2ECEA] hover:bg-[#FAFDFC]" data-testid={`payment-terms-row-${req.id}`}>
                        {paymentTermsCanDecide && (
                          <td className="px-3 py-3 w-8">
                            <input
                              type="checkbox"
                              checked={paymentTermsSelected.has(req.id)}
                              onChange={() => togglePaymentTermsSelect(req.id)}
                              className="w-4 h-4 accent-[#00a69c]"
                              data-testid={`payment-terms-select-${req.id}`}
                              aria-label={`Select ${req.lead_name || req.id}`}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="font-semibold text-[#052A23]">{req.lead_name || '—'}</div>
                          <div className="text-[11px] text-[#6D8E87]">{req.customer_name || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          {isEdit ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800" data-testid={`payment-terms-type-${req.id}`}>
                              Edit
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800" data-testid={`payment-terms-type-${req.id}`}>
                              New Won
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[#3B5A54] max-w-[380px]">
                          <div className="font-medium text-[#052A23]">{req.payment_terms_summary || '—'}</div>
                          {isEdit && req.previous_terms_summary && (
                            <div className="text-[11px] text-[#6D8E87] mt-1">
                              was: <span className="line-through">{req.previous_terms_summary}</span>
                            </div>
                          )}
                          <div className="mt-1 flex flex-wrap gap-1 text-[10px] uppercase tracking-wide">
                            {req.payment_terms?.advance_with_po?.enabled ? null : <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">No Advance</span>}
                            {req.payment_terms?.before_dispatch?.enabled ? null : <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">No Dispatch</span>}
                            {req.payment_terms?.on_installation?.enabled ? <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">On Install</span> : null}
                            {req.payment_terms?.credit?.enabled ? <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">Credit</span> : null}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-[#052A23] font-semibold">
                          {inr(Number(req.quotation_total || 0))}
                        </td>
                        <td className="px-4 py-3 text-[#3B5A54]">
                          <div>{req.requested_by_name || '—'}</div>
                          <div className="text-[11px] text-[#6D8E87]">{req.requested_by_role || ''}</div>
                        </td>
                        <td className="px-4 py-3 text-[#3B5A54]">
                          <span className={daysWaiting >= 3 ? 'text-rose-700 font-semibold' : 'text-[#3B5A54]'}>
                            {daysWaiting === 0 ? 'today' : `${daysWaiting}d`}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => navigate(tp(`/leads/${req.lead_id}`))}
                            className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs rounded border border-[#D5E8E4] text-[#3B5A54] hover:bg-[#F4F9F7]"
                            data-testid={`payment-terms-open-${req.id}`}
                            title="Open lead"
                          >
                            <Eye size={12} weight="bold" /> Open
                          </button>
                          {paymentTermsCanDecide ? (
                            <>
                              <button
                                type="button"
                                disabled={paymentTermsBusyId === req.id}
                                onClick={() => handlePaymentTermsDecision(req, 'approve')}
                                className="inline-flex items-center gap-1 px-2 py-1 mr-1 text-xs font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                                data-testid={`payment-terms-approve-${req.id}`}
                              >
                                <Check size={12} weight="bold" /> Approve
                              </button>
                              <button
                                type="button"
                                disabled={paymentTermsBusyId === req.id}
                                onClick={() => handlePaymentTermsDecision(req, 'reject')}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60"
                                data-testid={`payment-terms-reject-${req.id}`}
                              >
                                <X size={12} weight="bold" /> Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-[11px] text-[#6D8E87] italic">VP-only decision</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      )}
      {showModal && (
        <Dialog open={showModal} onOpenChange={() => setShowModal(false)}>
          <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="approval-modal">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit' }}>
                {action === 'approve' ? 'Approve' : 'Reject'} Quotation
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-[#3B5A54]">
                Are you sure you want to {action} this quotation for <strong>{selectedQuotation?.lead_name}</strong>?
              </p>
              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">Comments</label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  rows="3"
                  placeholder="Add your comments (optional)"
                  data-testid="approval-comments"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={submitQuotationApproval}
                  className={action === 'approve' ? 'btn-primary flex-1' : 'flex-1 bg-[#EF4444] text-white py-3 rounded-full font-medium hover:bg-red-600 transition'}
                  data-testid="confirm-approval-button"
                >
                  Confirm {action === 'approve' ? 'Approval' : 'Rejection'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* LORD Reviews Tab Content */}
      {activeTab === 'lord' && (
        <>
          {lordReviews.length === 0 ? (
            <div className="medical-card p-12 text-center" data-testid="no-lord-reviews">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check size={32} className="text-red-400" weight="bold" />
              </div>
              <h3 className="text-xl font-semibold text-[#052A23] mb-2">No Pending Reviews</h3>
              <p className="text-[#6D8E87]">No lost order reviews pending your action</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="lord-reviews-list">
              {lordReviews.map((lord) => (
                <div key={lord.id} className="medical-card p-6 border-l-4 border-red-400" data-testid={`lord-review-${lord.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-xl font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
                          {lord.lead_name}
                        </h3>
                        <span className="px-3 py-1 bg-red-100 text-red-700 text-xs rounded-full font-semibold">LOST ORDER</span>
                      </div>
                      <p className="text-sm text-[#6D8E87]">{lord.company} {lord.customer_code && `(${lord.customer_code})`}</p>
                    </div>
                    <p className="text-sm text-[#6D8E87]">{new Date(lord.created_at).toLocaleDateString()}</p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                    <div className="p-3 bg-[#F4F9F7] rounded-lg">
                      <p className="text-xs text-[#6D8E87]">Lost To</p>
                      <p className="font-medium text-[#052A23]">{lord.q1_order_went_to}</p>
                    </div>
                    <div className="p-3 bg-[#F4F9F7] rounded-lg">
                      <p className="text-xs text-[#6D8E87]">Reason</p>
                      <p className="font-medium text-[#052A23]">{lord.q2_why_chose_them}</p>
                    </div>
                    <div className="p-3 bg-[#F4F9F7] rounded-lg">
                      <p className="text-xs text-[#6D8E87]">Pricing</p>
                      <p className="font-medium text-[#052A23]">{lord.q3_pricing_competitiveness}</p>
                    </div>
                    <div className="p-3 bg-[#F4F9F7] rounded-lg">
                      <p className="text-xs text-[#6D8E87]">Est. Value</p>
                      <p className="font-medium text-[#00a69c]">₹{(lord.estimated_order_value || 0).toLocaleString('en-IN')}</p>
                    </div>
                  </div>

                  <div className="p-3 bg-red-50 rounded-lg mb-4 text-sm">
                    <p className="text-xs text-red-600 font-semibold mb-1">Salesperson's Reflection (Section C)</p>
                    <p className="text-[#052A23] mb-1"><span className="text-[#6D8E87]">Key Reason:</span> {lord.key_reason_for_loss}</p>
                    <p className="text-[#052A23]"><span className="text-[#6D8E87]">Would do differently:</span> {lord.what_differently_next_time}</p>
                  </div>

                  <div className="text-xs text-[#6D8E87] mb-3">Submitted by: <span className="font-medium text-[#052A23]">{lord.submitted_by_name}</span></div>

                  <div className="flex gap-3 pt-3 border-t border-[#D5E8E4]">
                    <button
                      onClick={() => navigate(`/leads/${lord.lead_id}`)}
                      className="flex items-center gap-2 px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm text-[#3B5A54] hover:bg-gray-50 transition"
                      data-testid={`lord-view-lead-${lord.id}`}
                    >
                      <Eye size={16} /> View Lead
                    </button>
                    <div className="flex-1" />
                    <button
                      onClick={() => handleReviewLord(lord, 'reject')}
                      className="flex items-center gap-2 px-5 py-2 bg-red-50 text-[#EF4444] rounded-lg text-sm font-semibold hover:bg-red-100 transition"
                      data-testid={`lord-reject-btn-${lord.id}`}
                    >
                      <X size={16} weight="bold" /> Reject
                    </button>
                    <button
                      onClick={() => handleReviewLord(lord, 'approve')}
                      className="flex items-center gap-2 px-6 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-semibold hover:bg-[#00bfb3] transition"
                      data-testid={`lord-review-btn-${lord.id}`}
                    >
                      <Check size={16} weight="bold" /> Approve & Comment
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Bank Accounts Tab Content */}
      {activeTab === 'bank' && canApproveBankAccounts && (
        <>
          {pendingBankAccounts.length === 0 ? (
            <div className="medical-card p-12 text-center" data-testid="no-bank-approvals">
              <div className="w-16 h-16 bg-[#E2F3F0] rounded-full flex items-center justify-center mx-auto mb-4">
                <Bank size={32} className="text-[#00a69c]" weight="bold" />
              </div>
              <h3 className="text-xl font-semibold text-[#052A23] mb-2">No Pending Bank Accounts</h3>
              <p className="text-[#6D8E87]">All bank account additions have been reviewed</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="bank-approvals-list">
              {pendingBankAccounts.map((item) => (
                <div key={item.customer_id} className="medical-card p-6" data-testid={`bank-approval-${item.customer_id}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-semibold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{item.organisation_name}</h3>
                        <span className="px-2 py-0.5 bg-[#FEF3C7] text-[#92400E] text-xs rounded font-bold">{item.customer_code}</span>
                      </div>
                      <p className="text-sm text-[#6D8E87] mt-1">{item.total_accounts} total account(s) - {item.pending_accounts.length} pending approval</p>
                    </div>
                    <button onClick={() => navigate(`/customers/${item.customer_id}`)}
                      className="flex items-center gap-1 px-4 py-2 border border-[#D5E8E4] rounded-lg text-sm text-[#3B5A54] hover:bg-gray-50 transition"
                      data-testid={`bank-view-customer-${item.customer_id}`}>
                      <Eye size={16} /> View Customer
                    </button>
                  </div>
                  <div className="space-y-3">
                    {item.pending_accounts.map((ba, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid={`pending-bank-${item.customer_id}-${idx}`}>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1 text-sm">
                          <div><span className="text-[#6D8E87]">Bank:</span> <span className="font-medium text-[#052A23]">{ba.bank_name}</span></div>
                          <div><span className="text-[#6D8E87]">A/C:</span> <span className="font-medium text-[#052A23]">{ba.account_number}</span></div>
                          <div><span className="text-[#6D8E87]">IFSC:</span> <span className="font-medium text-[#052A23]">{ba.ifsc_code}</span></div>
                          <div><span className="text-[#6D8E87]">Branch:</span> <span className="font-medium text-[#052A23]">{ba.branch}</span></div>
                        </div>
                        <div className="ml-4 flex gap-2">
                          <button onClick={() => handleRejectBankAccount(item.customer_id, ba.account_number)}
                            className="flex items-center gap-2 px-4 py-2 bg-red-50 text-[#EF4444] rounded-lg text-sm font-semibold hover:bg-red-100 transition"
                            data-testid={`reject-bank-btn-${item.customer_id}-${idx}`}>
                            <X size={16} weight="bold" /> Reject
                          </button>
                          <button onClick={() => handleApproveBankAccount(item.customer_id, ba.account_number)}
                            className="flex items-center gap-2 px-5 py-2 bg-[#00a69c] text-white rounded-lg text-sm font-semibold hover:bg-[#00bfb3] transition"
                            data-testid={`approve-bank-btn-${item.customer_id}-${idx}`}>
                            <Check size={16} weight="bold" /> Approve
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* LORD Review Modal */}
      {showLordModal && selectedLord && (
        <Dialog open={showLordModal} onOpenChange={() => setShowLordModal(false)}>
          <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto" data-testid="lord-review-modal">
            <DialogHeader>
              <DialogTitle className={`text-2xl font-bold ${lordAction === 'reject' ? 'text-red-700' : 'text-[#052A23]'}`} style={{ fontFamily: 'Outfit' }}>
                {lordAction === 'reject' ? 'Reject' : 'Review'} Lost Order - {selectedLord.lead_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#6D8E87]">Company:</span> <span className="font-medium">{selectedLord.company}</span></div>
                <div><span className="text-[#6D8E87]">Lost To:</span> <span className="font-medium">{selectedLord.q1_order_went_to}</span></div>
                <div><span className="text-[#6D8E87]">Why:</span> <span className="font-medium">{selectedLord.q2_why_chose_them}</span></div>
                <div><span className="text-[#6D8E87]">Pricing:</span> <span className="font-medium">{selectedLord.q3_pricing_competitiveness}</span></div>
                <div><span className="text-[#6D8E87]">Understanding:</span> <span className="font-medium">{selectedLord.q4_requirement_understanding}</span></div>
                <div><span className="text-[#6D8E87]">Engagement:</span> <span className="font-medium">{selectedLord.q5_engagement_till_final}</span></div>
                <div><span className="text-[#6D8E87]">Fight till last:</span> <span className="font-medium">{selectedLord.q6_fight_till_last}</span></div>
                <div><span className="text-[#6D8E87]">Relationship:</span> <span className="font-medium">{selectedLord.q9_relationship_strength}</span></div>
              </div>

              <div className="p-3 bg-red-50 rounded-lg text-sm">
                <p className="font-semibold text-red-700 mb-1">Salesperson's Reflection</p>
                <p className="mb-1"><span className="text-[#6D8E87]">Key Reason:</span> {selectedLord.key_reason_for_loss}</p>
                <p><span className="text-[#6D8E87]">What differently:</span> {selectedLord.what_differently_next_time}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#3B5A54] mb-2">
                  {lordAction === 'reject' ? 'Rejection Reason *' : 'Manager Comment (Section C) *'}
                </label>
                <textarea
                  value={lordComment}
                  onChange={(e) => setLordComment(e.target.value)}
                  className="w-full px-4 py-2 border border-[#D5E8E4] rounded-lg focus:ring-2 focus:ring-[#00bfb3] outline-none"
                  rows="4"
                  placeholder={lordAction === 'reject' ? 'Explain why this LORD review is being rejected...' : 'Add your review comments, observations, and recommendations...'}
                  data-testid="lord-manager-comment"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowLordModal(false)} className="flex-1 px-6 py-3 border border-[#D5E8E4] rounded-lg hover:bg-gray-50 transition">Cancel</button>
                <button onClick={submitLordReview} disabled={!lordComment.trim()}
                  className={`flex-1 py-3 text-white rounded-lg font-semibold transition disabled:opacity-40 ${lordAction === 'reject' ? 'bg-[#EF4444] hover:bg-red-600' : 'bg-[#00a69c] hover:bg-[#00bfb3]'}`}
                  data-testid="lord-submit-review-btn">
                  {lordAction === 'reject' ? 'Confirm Rejection' : 'Submit Review'}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* iter127 — Inline approve preview drawer */}
      {previewQuote && (
        <QuotePreviewDrawer
          quotation={previewQuote}
          canSeeGm={canSeeGm}
          onClose={() => setPreviewQuote(null)}
          onApprove={() => { handleApproveQuotation(previewQuote, 'approve'); setPreviewQuote(null); }}
          onReject={() => { handleApproveQuotation(previewQuote, 'reject'); setPreviewQuote(null); }}
          openFullHref={tp(`/quotations/${previewQuote.id}`)}
          formatInr={(n) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// QuotePreviewDrawer — right-side drawer showing the full quote inline.
// Reuses the quotation object already loaded on the list (no extra fetch).
// ────────────────────────────────────────────────────────────────────────
function QuotePreviewDrawer({ quotation, canSeeGm, onClose, onApprove, onReject, openFullHref, formatInr }) {
  const q = quotation;
  // Line-item GM calc — mirrors the util already used inline on the card.
  const lineGm = (it) => {
    const rev = Number(it.discounted_amount || it.total || 0);
    const cogs = Number(it.cogs_at_sale || 0) * Number(it.quantity || 0);
    if (!(rev > 0 && cogs > 0)) return null;
    const gmPct = ((rev - cogs) / rev) * 100;
    return { rev, cogs, gmPct, gmAmt: rev - cogs };
  };
  const quoteGm = React.useMemo(() => {
    let rev = 0, cogs = 0, missing = 0;
    (q.items || []).forEach((it) => {
      const r = Number(it.discounted_amount || it.total || 0);
      const c = Number(it.cogs_at_sale || 0) * Number(it.quantity || 0);
      rev += r;
      if (c > 0) cogs += c; else if (r > 0) missing += 1;
    });
    if (rev <= 0 || cogs <= 0) return { rev, cogs, missing, gmPct: null };
    return { rev, cogs, missing, gmPct: ((rev - cogs) / rev) * 100, gmAmt: rev - cogs };
  }, [q]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 flex justify-end"
      onClick={onClose}
      data-testid="preview-drawer"
    >
      <div
        className="bg-white w-full max-w-3xl h-full flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E2ECEA] flex items-start justify-between bg-[#F4F9F7] shrink-0">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Approval preview</div>
            <h2 className="text-xl font-bold text-[#052A23] truncate" style={{ fontFamily: 'Outfit' }}>{q.lead_name}</h2>
            <div className="text-[11px] text-[#6D8E87] mt-0.5 font-mono">
              {q.id.slice(0, 8)} · {q.created_by_name} · stage: <b className="text-[#052A23]">{q.current_approval_stage}</b>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <a
              href={openFullHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border border-[#00a69c] text-[#00857e] hover:bg-white"
              title="Open the full quotation page"
              data-testid="preview-drawer-full"
            >
              <ArrowSquareOut size={12} /> Full page
            </a>
            <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="preview-drawer-close">
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Reasons — surfaced right on top so approvers see the "why" first */}
          {q.approval_reasons && q.approval_reasons.length > 0 && (
            <div className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
              <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold mb-1.5">Why this needs your approval</div>
              <ul className="space-y-1">
                {q.approval_reasons.map((r, i) => (
                  <li key={i} className="text-xs text-[#3B5A54]"><b>{r.label}</b> — <span className="text-[#6D8E87]">{r.detail}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* Totals strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 rounded-lg border border-[#E2ECEA] bg-white">
              <div className="text-[10px] uppercase tracking-widest text-[#6D8E87]">Subtotal</div>
              <div className="text-lg font-bold text-[#052A23]">{formatInr(q.subtotal)}</div>
            </div>
            <div className="p-3 rounded-lg border border-[#E2ECEA] bg-white">
              <div className="text-[10px] uppercase tracking-widest text-[#6D8E87]">Discount</div>
              <div className="text-lg font-bold text-rose-700">{q.discount ? `${q.discount}%` : '—'}</div>
            </div>
            <div className="p-3 rounded-lg border border-[#E2ECEA] bg-white">
              <div className="text-[10px] uppercase tracking-widest text-[#6D8E87]">Tax</div>
              <div className="text-lg font-bold text-[#3B5A54]">{formatInr(q.total_tax)}</div>
            </div>
            <div className="p-3 rounded-lg border border-[#00a69c] bg-[#F4F9F7]">
              <div className="text-[10px] uppercase tracking-widest text-[#00857e] font-bold">Total</div>
              <div className="text-lg font-bold text-[#00857e]">{formatInr(q.total)}</div>
            </div>
          </div>

          {/* GM summary — VP+/President only */}
          {canSeeGm && (
            <div className="p-3 rounded-lg border border-[#F59E0B]/30 bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7]">
              <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold mb-1">Executive Margin Preview</div>
              {quoteGm.gmPct === null ? (
                <div className="text-xs text-[#92400E]">COGS not set on {quoteGm.missing} item{quoteGm.missing === 1 ? '' : 's'} — margin unavailable.</div>
              ) : (
                <div className="flex flex-wrap items-center gap-4 text-xs text-[#3B5A54]">
                  <span>Revenue <b>{formatInr(quoteGm.rev)}</b></span>
                  <span>COGS <b>{formatInr(quoteGm.cogs)}</b></span>
                  <span className={`font-bold ${quoteGm.gmPct >= 30 ? 'text-[#065F46]' : quoteGm.gmPct >= 15 ? 'text-[#92400E]' : 'text-[#991B1B]'}`}>
                    GM {quoteGm.gmPct.toFixed(1)}% · {formatInr(quoteGm.gmAmt)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Line items */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold mb-2">Line items ({(q.items || []).length})</div>
            <div className="border border-[#E2ECEA] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F9F7] text-[10px] uppercase tracking-wide text-[#3B5A54]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Product</th>
                    <th className="text-right px-3 py-2 font-semibold">Qty</th>
                    <th className="text-right px-3 py-2 font-semibold">Rate</th>
                    <th className="text-right px-3 py-2 font-semibold">Disc</th>
                    <th className="text-right px-3 py-2 font-semibold">Tax</th>
                    {canSeeGm && <th className="text-right px-3 py-2 font-semibold">GM %</th>}
                    <th className="text-right px-3 py-2 font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.items || []).map((it, idx) => {
                    const g = canSeeGm ? lineGm(it) : null;
                    return (
                      <tr key={idx} className="border-t border-[#F0F5F3] hover:bg-[#F9FCFB]">
                        <td className="px-3 py-2 text-[#052A23]">
                          <div className="font-medium">{it.product_name}</div>
                          {it.product_code && <div className="font-mono text-[10px] text-[#6D8E87]">#{it.product_code}</div>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                        <td className="px-3 py-2 text-right font-mono text-[#3B5A54]">{formatInr(it.price)}</td>
                        <td className="px-3 py-2 text-right text-[#3B5A54] text-xs">
                          {it.item_discount_percent ? `${it.item_discount_percent}%` : '—'}
                          {it.item_discount_amount ? <div className="text-[10px] text-rose-700 font-mono">-{formatInr(it.item_discount_amount)}</div> : null}
                        </td>
                        <td className="px-3 py-2 text-right text-xs text-[#3B5A54]">{it.tax_rate ? `${it.tax_rate}%` : '—'}</td>
                        {canSeeGm && (
                          <td className="px-3 py-2 text-right">
                            {g && g.gmPct != null ? (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${g.gmPct >= 30 ? 'bg-[#D1FAE5] text-[#065F46]' : g.gmPct >= 15 ? 'bg-[#FEF3C7] text-[#92400E]' : 'bg-[#FEE2E2] text-[#991B1B]'}`}>
                                {g.gmPct.toFixed(1)}%
                              </span>
                            ) : <span className="text-[10px] text-[#94A3B8] italic">—</span>}
                          </td>
                        )}
                        <td className="px-3 py-2 text-right font-semibold text-[#052A23]">{formatInr(it.total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Terms grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            {q.payment_terms && <Term label="Payment terms" value={q.payment_terms} />}
            {q.delivery_days != null && <Term label="Delivery" value={`${q.delivery_days} days`} />}
            {q.warranty_years != null && <Term label="Warranty" value={`${q.warranty_years} year${q.warranty_years === 1 ? '' : 's'}`} />}
            {q.installation && <Term label="Installation" value={q.installation} />}
            {q.transportation && <Term label="Transportation" value={q.transportation} />}
            {q.expected_closing_date && <Term label="Expected close" value={new Date(q.expected_closing_date).toLocaleDateString('en-IN')} />}
          </div>
        </div>

        {/* Sticky action footer */}
        <div className="border-t border-[#D5E8E4] px-5 py-3 bg-white shrink-0 flex gap-3">
          <button
            type="button"
            onClick={onReject}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-[#EF4444] rounded-full font-medium hover:bg-red-100"
            data-testid="preview-drawer-reject"
          >
            <X size={18} weight="bold" /> Reject
          </button>
          <button
            type="button"
            onClick={onApprove}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00a69c] text-white rounded-full font-medium hover:bg-[#00bfb3]"
            data-testid="preview-drawer-approve"
          >
            <Check size={18} weight="bold" /> Approve
          </button>
        </div>
      </div>
    </div>
  );
}

function Term({ label, value }) {
  return (
    <div className="p-2.5 border border-[#E2ECEA] rounded-lg bg-white">
      <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">{label}</div>
      <div className="text-sm text-[#052A23] font-medium mt-0.5 break-words">{value}</div>
    </div>
  );
}

export default Approvals;
